import { FETCH_ERROR } from "@boark/shared/consts/fetch-error";
import { BoardMeetingState } from "@boark/shared/types/board-meeting";
import { BoardMeetingCommentCreate } from "@boark/shared/types/board-meeting-comment-create";
import { BoardMeetingCommentUpdate } from "@boark/shared/types/board-meeting-comment-update";
import { BoardMeetingCommentWithCreatedBy } from "@boark/shared/types/board-meeting-comment-with-created-by";
import { BoardMeetingWithDetails } from "@boark/shared/types/board-meeting-with-details";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  createBoardMeetingComment,
  deleteBoardMeetingComment,
  getBoardMeeting,
  getBoardMeetingComments,
  getBoardMeetings,
  updateBoardMeetingComment,
} from "../services/board-meeting";
import { recordIdToId } from "../utils/record-id-to-id";

// Keys use the id without table prefix so route params and record ids match
export function boardMeetingQueryKey(meetingId: string) {
  return ["board-meeting", recordIdToId(meetingId)] as const;
}

export function boardMeetingCommentsQueryKey(meetingId: string) {
  return ["board-meeting-comments", recordIdToId(meetingId)] as const;
}

export function useGetBoardMeetings() {
  return useQuery({
    queryKey: ["board-meetings"],
    queryFn: () => getBoardMeetings(),
    staleTime: 1000 * 60,
    retry: (_failureCount, error: unknown) =>
      error !== FETCH_ERROR.UNAUTHORIZED,
  });
}

// Last queued request per meeting. Requests for a meeting run one at a time
// so a response read before another write can't overwrite newer data.
const meetingRequestQueues = new Map<string, Promise<unknown>>();

function enqueueMeetingRequest<T>(
  meetingId: string,
  request: () => Promise<T>,
): Promise<T> {
  const key = recordIdToId(meetingId);
  const previous = meetingRequestQueues.get(key) ?? Promise.resolve();
  const next = previous.catch(() => undefined).then(request);
  meetingRequestQueues.set(key, next);
  return next;
}

// Several people work on a meeting at the same time, so it is polled while
// it can still change
const MEETING_POLL_INTERVAL = 5000;

export function useGetBoardMeeting(meetingId: string) {
  return useQuery({
    queryKey: boardMeetingQueryKey(meetingId),
    queryFn: () =>
      enqueueMeetingRequest(meetingId, () => getBoardMeeting(meetingId)),
    refetchInterval: (query) =>
      query.state.data?.state === BoardMeetingState.COMPLETE
        ? false
        : MEETING_POLL_INTERVAL,
    retry: (_failureCount, error: unknown) =>
      error !== FETCH_ERROR.UNAUTHORIZED,
  });
}

// Runs a request that returns the updated meeting and stores it in the cache
export function useBoardMeetingMutation<TVariables>(
  meetingId: string,
  mutationFn: (variables: TVariables) => Promise<BoardMeetingWithDetails>,
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (variables: TVariables) => {
      const meeting = await enqueueMeetingRequest(meetingId, () =>
        mutationFn(variables),
      );
      queryClient.setQueryData(boardMeetingQueryKey(meetingId), meeting);
      await queryClient.invalidateQueries({ queryKey: ["board-meetings"] });
      return meeting;
    },
    // A failed action usually means the page is out of date, e.g. someone
    // else changed the state, so catch up right away
    onError: () =>
      queryClient.invalidateQueries({
        queryKey: boardMeetingQueryKey(meetingId),
      }),
  });
}

export function useGetBoardMeetingComments(meetingId: string) {
  return useQuery({
    queryKey: boardMeetingCommentsQueryKey(meetingId),
    queryFn: () => getBoardMeetingComments(meetingId),
    refetchInterval: MEETING_POLL_INTERVAL,
    retry: (_failureCount, error: unknown) =>
      error !== FETCH_ERROR.UNAUTHORIZED,
  });
}

export function useCreateBoardMeetingComment(meetingId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      contentId,
      comment,
    }: {
      contentId: string;
      comment: BoardMeetingCommentCreate;
    }) => {
      const created = await createBoardMeetingComment(
        meetingId,
        contentId,
        comment,
      );
      queryClient.setQueryData(
        boardMeetingCommentsQueryKey(meetingId),
        (existing: BoardMeetingCommentWithCreatedBy[] | undefined) => [
          ...(existing ?? []),
          created,
        ],
      );
      return created;
    },
  });
}

export function useUpdateBoardMeetingComment(meetingId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      commentId,
      comment,
    }: {
      commentId: string;
      comment: BoardMeetingCommentUpdate;
    }) => {
      const updated = await updateBoardMeetingComment(
        meetingId,
        commentId,
        comment,
      );
      queryClient.setQueryData(
        boardMeetingCommentsQueryKey(meetingId),
        (existing: BoardMeetingCommentWithCreatedBy[] | undefined) =>
          existing?.map((item) =>
            item.id === updated.id ? updated : item,
          ) ?? [updated],
      );
      return updated;
    },
  });
}

export function useDeleteBoardMeetingComment(meetingId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (commentId: string) => {
      await deleteBoardMeetingComment(meetingId, commentId);
      queryClient.setQueryData(
        boardMeetingCommentsQueryKey(meetingId),
        (existing: BoardMeetingCommentWithCreatedBy[] | undefined) =>
          existing?.filter((item) => item.id !== commentId) ?? [],
      );
    },
  });
}
